import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Coins, MonitorSmartphone, Package, Target, Zap } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { KpiCard } from '../../../../components/design-system/admin/KpiCard';
import { RelativeTime } from '../../../../components/design-system/admin/RelativeTime';
import { formatNumber } from '../../../../components/design-system/admin/format';
import { QuotaBar } from '../../../../components/design-system/admin/QuotaBar';
import { useAdminOverviewApi } from '../../../../common/hooks/use-admin-overview-api.hook';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { useDevicesApi } from '../../../../common/hooks/use-devices-api.hook';
import type { SeatOverview } from '../../../../common/hooks/use-devices-api.hook';
import type { OverviewVendorRow } from '../../../../common/models/admin-overview.model';

/** The window the overview numbers cover. */
const DAYS = 30;

type Load<T> = { state: 'loading' } | { state: 'ready'; value: T } | { state: 'failed' };

interface VendorOverviewPanelProps {
    vendorId: string;
    /** Bumped by the page when credits change elsewhere, to re-read the balance. */
    creditsVersion?: number;
}

/**
 * How this vendor is doing, from the platform overview (last 30 days) plus their
 * credit balance and accounts. Every number degrades to '—' on its own when its
 * source fails, so one slow service never blanks the tab.
 */
export function VendorOverviewPanel({ vendorId, creditsVersion = 0 }: VendorOverviewPanelProps) {
    const { t } = useTranslation();
    const overviewApi = useAdminOverviewApi();
    const csApi = useContentStudioApi();
    const devicesApi = useDevicesApi();
    // API hooks return new objects every render; the fetches must not follow them.
    const apis = useRef({ overviewApi, csApi, devicesApi });
    apis.current = { overviewApi, csApi, devicesApi };

    const [row, setRow] = useState<Load<OverviewVendorRow | null>>({ state: 'loading' });
    const [credits, setCredits] = useState<Load<number>>({ state: 'loading' });
    const [seats, setSeats] = useState<Load<SeatOverview>>({ state: 'loading' });

    useEffect(() => {
        let alive = true;
        setRow({ state: 'loading' });
        setSeats({ state: 'loading' });
        apis.current.overviewApi
            .getOverview(DAYS)
            .then((o) => alive && setRow({ state: 'ready', value: o?.vendors?.find((v) => v.id === vendorId) ?? null }))
            .catch(() => alive && setRow({ state: 'failed' }));
        apis.current.devicesApi
            .getVendorOverview(vendorId)
            .then((s) => alive && setSeats({ state: 'ready', value: s }))
            .catch(() => alive && setSeats({ state: 'failed' }));
        return () => {
            alive = false;
        };
    }, [vendorId]);

    useEffect(() => {
        let alive = true;
        setCredits({ state: 'loading' });
        apis.current.csApi
            .adminGetCreditsForVendor(vendorId)
            .then((r) => alive && setCredits({ state: 'ready', value: r?.balance }))
            .catch(() => alive && setCredits({ state: 'failed' }));
        return () => {
            alive = false;
        };
    }, [vendorId, creditsVersion]);

    const v = row.state === 'ready' ? row.value : null;
    const unavailable = row.state === 'failed' || (row.state === 'ready' && !row.value);
    const dash = '—';
    const quota = v?.quota;
    // The vendor's accounts against its plan (seats are accounts).
    const accounts = seats.state === 'ready' ? seats.value?.seats ?? null : null;

    return (
        <div className="flex flex-col gap-4" data-testid="vendor-overview">
            {unavailable && (
                <p className="text-xs text-muted-foreground" data-testid="vendor-overview-unavailable">
                    {t('ADMIN_VENDOREDIT.OVERVIEW.UNAVAILABLE')}
                </p>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                    label={t('ADMIN_VENDOREDIT.OVERVIEW.VISUALIZATIONS')}
                    value={v ? formatNumber(v.visualizations) : dash}
                    delta={v ? { current: v.visualizations, previous: v.visualizationsPrevious } : undefined}
                    hint={t('ADMIN_SHELL.COMMON.VS_PREVIOUS', { days: DAYS })}
                    icon={<Zap />}
                    loading={row.state === 'loading'}
                    testId="vendor-kpi-visualizations"
                />
                <KpiCard
                    label={t('ADMIN_VENDOREDIT.OVERVIEW.LEADS')}
                    value={v ? formatNumber(v.leads) : dash}
                    hint={t('ADMIN_VENDOREDIT.OVERVIEW.LAST_DAYS', { days: DAYS })}
                    icon={<Target />}
                    loading={row.state === 'loading'}
                    testId="vendor-kpi-leads"
                />
                <KpiCard
                    label={t('ADMIN_VENDOREDIT.OVERVIEW.PRODUCTS')}
                    value={v ? formatNumber(v.products) : dash}
                    hint={t('ADMIN_VENDOREDIT.OVERVIEW.PRODUCTS_HINT')}
                    icon={<Package />}
                    loading={row.state === 'loading'}
                    testId="vendor-kpi-products"
                />
                <KpiCard
                    label={t('ADMIN_VENDOREDIT.OVERVIEW.LAST_ACTIVITY')}
                    value={v ? <RelativeTime value={v.lastActivityAt} className="text-2xl" /> : dash}
                    hint={t('ADMIN_VENDOREDIT.OVERVIEW.LAST_ACTIVITY_HINT')}
                    icon={<Activity />}
                    loading={row.state === 'loading'}
                    testId="vendor-kpi-last-activity"
                />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {/* The monthly visualization allowance: used against the plan's limit (-1 = unlimited). */}
                <Card className="h-full gap-0 px-4 py-4" data-testid="vendor-kpi-quota">
                    <p className="text-sm text-muted-foreground">{t('ADMIN_VENDOREDIT.OVERVIEW.QUOTA')}</p>
                    {row.state === 'loading' ? (
                        <>
                            <Skeleton className="mt-3 h-8 w-24" />
                            <Skeleton className="mt-3 h-1.5 w-full" />
                        </>
                    ) : quota ? (
                        <>
                            <p className="mt-2 flex items-baseline gap-1.5">
                                <span className="font-heading text-3xl leading-none font-medium tracking-tight tabular-nums">
                                    {formatNumber(quota.used)}
                                </span>
                                <span className="text-sm text-muted-foreground tabular-nums">
                                    {quota.limit < 0
                                        ? t('ADMIN_VENDOREDIT.OVERVIEW.QUOTA_UNLIMITED')
                                        : t('ADMIN_VENDOREDIT.OVERVIEW.QUOTA_OF', { limit: formatNumber(quota.limit) })}
                                </span>
                            </p>
                            {quota.limit >= 0 && <QuotaBar used={quota.used} limit={quota.limit} className="mt-3" />}
                            <p className="mt-1.5 text-xs text-muted-foreground">{t('ADMIN_VENDOREDIT.OVERVIEW.QUOTA_HINT')}</p>
                        </>
                    ) : (
                        <p className="mt-2 font-heading text-3xl leading-none font-medium tracking-tight">{dash}</p>
                    )}
                </Card>

                <KpiCard
                    label={t('ADMIN_VENDOREDIT.OVERVIEW.CREDITS')}
                    value={credits.state === 'ready' && credits.value != null ? formatNumber(credits.value) : dash}
                    hint={t('ADMIN_VENDOREDIT.OVERVIEW.CREDITS_HINT')}
                    icon={<Coins />}
                    href="?tab=plan"
                    loading={credits.state === 'loading'}
                    testId="vendor-kpi-credits"
                />
                <KpiCard
                    label={t('ADMIN_VENDOREDIT.OVERVIEW.SEATS')}
                    value={accounts ? formatNumber(accounts.used) : dash}
                    hint={
                        accounts
                            ? accounts.limit < 0
                                ? t('ADMIN_VENDOREDIT.OVERVIEW.SEATS_UNLIMITED')
                                : t('ADMIN_VENDOREDIT.OVERVIEW.SEATS_OF', { limit: formatNumber(accounts.limit) })
                            : t('ADMIN_VENDOREDIT.OVERVIEW.SEATS_HINT')
                    }
                    icon={<MonitorSmartphone />}
                    href="?tab=devices"
                    loading={seats.state === 'loading'}
                    testId="vendor-kpi-seats"
                />
            </div>
        </div>
    );
}
